import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import * as PolicyActions from './policy.actions';
import * as PolicySelectors from './policy.selectors';
import { PolicyService } from '../services/policy.service';
import {
  approvalRequestRoute,
  policySaveOutcome,
} from '../services/policy-save-outcome';
import { ToastService } from '@shared/components/toast/services/toast.service';
import { PolicyListService } from '../services/policy-list.service';
import { GetPoliciesQuery, PolicyCategory } from '../models/policy-list.model';
// TODO: Replace console.log with proper logging service

/**
 * Policy Effects
 * Handles side effects for policy-related actions
 */
@Injectable()
export class PolicyEffects {
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  /**
   * Load policy schema effect
   * Triggers API call when loadPolicySchema action is dispatched
   */
  loadPolicySchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.loadPolicySchema),
      switchMap(() =>
        this.policyService.getPolicySchema().pipe(
          map(response =>
            PolicyActions.loadPolicySchemaSuccess({ schema: response.data })
          ),
          catchError(error =>
            of(
              PolicyActions.loadPolicySchemaFailure({
                error: this.failureMessage(
                  error,
                  'policies.errors.loadSchemaFailed'
                ),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Load users effect
   * Triggers API call when loadUsers action is dispatched
   */
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.loadUsers),
      switchMap(() =>
        this.policyService.getUsers().pipe(
          map(response =>
            PolicyActions.loadUsersSuccess({ users: response.data })
          ),
          catchError(error =>
            of(
              PolicyActions.loadUsersFailure({
                error: this.failureMessage(
                  error,
                  'policies.errors.loadUsersFailed'
                ),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Submit policy effect
   * Triggers API call when submitPolicy action is dispatched
   */
  submitPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.submitPolicy),
      switchMap(({ payload }) =>
        this.policyService.createPolicy(payload).pipe(
          map(response => PolicyActions.submitPolicySuccess({ response })),
          catchError(error =>
            of(
              PolicyActions.submitPolicyFailure({
                error: this.failureMessage(
                  error,
                  'policies.errors.createFailed'
                ),
              })
            )
          )
        )
      )
    )
  );

  /**
   * Handle successful policy submission
   * Navigate to policies list or show success message
   */
  submitPolicySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PolicyActions.submitPolicySuccess),
        tap(({ response }) => {
          // A policy queued for approval was not created: say so and open
          // the request, rather than returning to a list without it (the
          // create-side sibling of F-158).
          const outcome = policySaveOutcome(response);
          if (outcome.kind === 'queued') {
            this.toast.success(
              this.translate.instant('policies.create.sentForApproval')
            );
            this.router.navigate(
              approvalRequestRoute(outcome.approvalRequestId)
            );
            return;
          }
          if (outcome.message) this.toast.success(outcome.message);
          this.router.navigate(['/dashboard/policies']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle failed policy submission
   * Show error message
   */
  submitPolicyFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PolicyActions.submitPolicyFailure),
        tap(() => {
          // TODO: Show error modal/notification
        })
      ),
    { dispatch: false }
  );

  /**
   * Load policies effect
   * Triggers API call when loadPolicies action is dispatched
   */
  loadPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.loadPolicies),
      withLatestFrom(
        this.store.select(PolicySelectors.selectPagination),
        this.store.select(PolicySelectors.selectFilters),
        this.store.select(PolicySelectors.selectSort)
      ),
      switchMap(([action, pagination, filters, sort]) => {
        const query: GetPoliciesQuery = {
          page: pagination.page,
          limit: pagination.limit,
          status: filters.status,
          ...(filters.domain
            ? { category: filters.domain as PolicyCategory }
            : {}),
          orderBy: sort.sortBy,
          order: sort.sortType,
          ...action.query, // Allow override from action
        };

        return this.policyListService.getPolicies(query).pipe(
          map(response =>
            PolicyActions.loadPoliciesSuccess({
              policies: response.data,
              pagination: response.pagination,
            })
          ),
          catchError(error =>
            of(
              PolicyActions.loadPoliciesFailure({
                error: this.failureMessage(
                  error,
                  'policies.list.errors.loadFailed'
                ),
              })
            )
          )
        );
      })
    )
  );

  /**
   * Reload policies when filters, sort, or pagination changes
   */
  reloadOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PolicyActions.setPage,
        PolicyActions.setPageSize,
        PolicyActions.setStatusFilter,
        PolicyActions.setDomainFilter,
        PolicyActions.setSort
      ),
      map(() => PolicyActions.loadPolicies({}))
    )
  );

  constructor(
    private actions$: Actions,
    private policyService: PolicyService,
    private policyListService: PolicyListService,
    private router: Router,
    private store: Store
  ) {}

  /** The server's sentence, else this failure's localized fallback (F-149). */
  private failureMessage(error: unknown, fallbackKey: string): string {
    return extractErrorMessage(error, this.translate.instant(fallbackKey));
  }
}
